/**
 * WallConfirmation Component
 * Review/confirmation page showing all answered questions and entered data for a wall
 * Allows editing individual sections before confirming and moving to next wall
 */

import React, { useState } from "react";
import FilePreviewModalSimple from "../FileUpload/FilePreviewModalSimple";

const WallConfirmation = ({
  currentData,
  onEdit,
  onConfirm,
  onBack,
  canGoBack = false,
  isCompleted = false,
}) => {
  const [previewImage, setPreviewImage] = useState(null);
  const renderYesNoAnswer = (label, value, editKey) => {
    if (value === undefined || value === null) return null;

    return (
      <div style={{ marginBottom: "15px" }}>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
            <span
              style={{
                fontWeight: "bold",
                fontSize: "15px",
                color: "var(--text-primary)",
              }}
            >
              {label}
            </span>
            <div style={{ display: "flex", gap: "10px" }}>
              <button
                disabled
                style={{
                  padding: "8px 25px",
                  fontSize: "14px",
                  border: "1px solid var(--border-primary)",
                  backgroundColor:
                    value === true
                      ? "var(--surface-hover)"
                      : "var(--surface-secondary)",
                  cursor: "not-allowed",
                  fontWeight: value === true ? "bold" : "normal",
                  color: "var(--text-primary)",
                }}
              >
                Yes
              </button>
              <button
                disabled
                style={{
                  padding: "8px 25px",
                  fontSize: "14px",
                  border: "1px solid var(--border-primary)",
                  backgroundColor:
                    value === false
                      ? "var(--surface-hover)"
                      : "var(--surface-secondary)",
                  cursor: "not-allowed",
                  fontWeight: value === false ? "bold" : "normal",
                  color: "var(--text-primary)",
                }}
              >
                No
              </button>
            </div>
          </div>
          <button
            onClick={() => onEdit(editKey)}
            style={{
              padding: "6px 20px",
              fontSize: "14px",
              border: "1px solid var(--border-primary)",
              backgroundColor: "var(--surface-secondary)",
              color: "var(--text-primary)",
              cursor: "pointer",
            }}
          >
            Edit
          </button>
        </div>
      </div>
    );
  };

  const renderDataSection = (title, data, editKey) => {
    // Filter out null/undefined/empty values from the data object
    const filteredData = {};
    Object.entries(data).forEach(([key, value]) => {
      // Skip N/A flag fields
      if (key.includes("_na")) return;

      // Skip fields where corresponding _na field is true
      const naFieldName = `${key}_na`;
      if (currentData[naFieldName] === true) return;

      // Skip fields with 'N/A' value
      if (value === "N/A") return;

      // Skip null, undefined, or empty string values
      if (value === null || value === undefined || value === "") return;

      // Skip empty arrays
      if (Array.isArray(value) && value.length === 0) return;

      // Include the value
      filteredData[key] = value;
    });

    if (Object.keys(filteredData).length === 0) return null;

    return (
      <div
        style={{
          marginBottom: "25px",
          padding: "15px",
          backgroundColor: "var(--surface-tertiary)",
          color: "var(--text-primary)",
          border: "1px solid var(--border-secondary)",
          borderRadius: "4px",
        }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: "15px",
          }}
        >
          <h4
            style={{
              margin: 0,
              fontSize: "16px",
              color: "var(--text-primary)",
            }}
          >
            {title}
          </h4>
          <button
            onClick={() => onEdit(editKey)}
            style={{
              padding: "6px 20px",
              fontSize: "14px",
              border: "1px solid var(--border-primary)",
              backgroundColor: "var(--surface-secondary)",
              color: "var(--text-primary)",
              cursor: "pointer",
            }}
          >
            Edit
          </button>
        </div>

        <div style={{ display: "grid", gap: "10px" }}>
          {Object.entries(filteredData).map(([key, value]) => {
            // Format the key name
            const formattedKey = key
              .replace(/_/g, " ")
              .replace(/\b\w/g, (char) => char.toUpperCase());

            // Handle different value types
            let displayValue = value;
            let isImageField = false;

            if (Array.isArray(value)) {
              isImageField = key.includes("image");
              if (!isImageField) {
                displayValue = `${value.length} item(s)`;
              }
            } else if (typeof value === "boolean") {
              displayValue = value ? "Yes" : "No";
            }

            // Render image thumbnails
            if (isImageField && Array.isArray(value) && value.length > 0) {
              return (
                <div key={key} style={{ marginBottom: "15px" }}>
                  <span
                    style={{
                      fontWeight: "500",
                      display: "block",
                      marginBottom: "8px",
                      color: "var(--text-primary)",
                    }}
                  >
                    {formattedKey}:
                  </span>
                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns:
                        "repeat(auto-fill, minmax(100px, 1fr))",
                      gap: "10px",
                      marginTop: "5px",
                    }}
                  >
                    {value.map((image, idx) => (
                      <div
                        key={image.id || idx}
                        style={{
                          border: "1px solid var(--border-secondary)",
                          padding: "5px",
                          backgroundColor: "var(--surface-secondary)",
                        }}
                      >
                        <img
                          src={image.url}
                          alt={
                            image.label || image.filename || `Image ${idx + 1}`
                          }
                          style={{
                            width: "100%",
                            height: "80px",
                            objectFit: "cover",
                            display: "block",
                            cursor: "pointer",
                          }}
                          onClick={() => setPreviewImage(image)}
                          onError={(e) => {
                            e.target.style.display = "none";
                            e.target.nextSibling.style.display = "flex";
                          }}
                        />
                        <div
                          style={{
                            display: "none",
                            height: "80px",
                            alignItems: "center",
                            justifyContent: "center",
                            backgroundColor: "var(--surface-tertiary)",
                            color: "var(--text-secondary)",
                            fontSize: "12px",
                          }}
                        >
                          Image
                        </div>
                        <p
                          style={{
                            margin: "5px 0 0 0",
                            fontSize: "11px",
                            color: "var(--text-secondary)",
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                          }}
                          title={image.label || image.filename}
                        >
                          {image.label || image.filename || `Image ${idx + 1}`}
                        </p>
                      </div>
                    ))}
                  </div>
                </div>
              );
            }

            return (
              <div key={key} style={{ display: "flex", gap: "10px" }}>
                <span style={{ fontWeight: "500", minWidth: "200px" }}>
                  {formattedKey}:
                </span>
                <span>{displayValue}</span>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  return (
    <div className="wall-confirmation" style={{ padding: "20px" }}>
      <div
        style={{
          backgroundColor: "var(--surface-secondary)",
          color: "var(--text-primary)",
          border: "1px solid var(--border-secondary)",
          padding: "20px",
          borderRadius: "4px",
        }}
      >
        {/* Wall Name */}
        {currentData.wall_name && (
          <div
            style={{
              marginBottom: "25px",
              paddingBottom: "15px",
              borderBottom: "2px solid var(--border-secondary)",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <h3
              style={{
                margin: 0,
                fontSize: "18px",
                color: "var(--text-primary)",
              }}
            >
              {currentData.wall_name}
            </h3>
            <button
              onClick={() => onEdit("wall_name")}
              style={{
                padding: "6px 20px",
                fontSize: "14px",
                border: "1px solid var(--border-primary)",
                backgroundColor: "var(--surface-secondary)",
                color: "var(--text-primary)",
                cursor: "pointer",
              }}
            >
              Edit
            </button>
          </div>
        )}

        {/* Yes/No Questions */}
        <div style={{ marginBottom: "25px" }}>
          {renderYesNoAnswer(
            "Is there a profile view for the wall?",
            currentData.has_profile_view,
            "has_profile_view",
          )}
          {renderYesNoAnswer(
            "Are there plan view top and bottom of wall callouts?",
            currentData.has_plan_view_callouts,
            "has_plan_view_callouts",
          )}
          {renderYesNoAnswer(
            "Is there a wall table?",
            currentData.has_wall_table,
            "has_wall_table",
          )}
          {renderYesNoAnswer(
            "Are there only contours for the wall?",
            currentData.only_contours,
            "only_contours",
          )}
        </div>

        {/* Profile View Data */}
        {currentData.has_profile_view &&
          renderDataSection(
            "Wall Profile Details",
            {
              profile_top_images: currentData.profile_top_images,
            },
            "wall_profile_form",
          )}

        {/* Plan View Callouts Data */}
        {currentData.has_plan_view_callouts &&
          renderDataSection(
            "Plan View Callouts Details",
            {
              plan_view_images: currentData.plan_view_images,
            },
            "wall_plan_view_form",
          )}

        {/* Wall Table Data */}
        {currentData.has_wall_table &&
          renderDataSection(
            "Wall Table Details",
            {
              table_images: currentData.table_images,
            },
            "wall_table_form",
          )}

        {/* Contours Data */}
        {currentData.only_contours &&
          renderDataSection(
            "Wall Contours Details",
            {
              contour_images: currentData.contour_images,
            },
            "wall_contours_form",
          )}
      </div>

      {/* Action Buttons */}
      <div
        style={{
          marginTop: "25px",
          display: "flex",
          gap: "15px",
          alignItems: "center",
        }}
      >
        {canGoBack && (
          <button
            onClick={onBack}
            style={{
              padding: "12px 40px",
              fontSize: "16px",
              border: "1px solid var(--border-primary)",
              backgroundColor: "var(--surface-tertiary)",
              color: "var(--text-primary)",
              cursor: "pointer",
            }}
          >
            ← Back
          </button>
        )}

        {/* Only show Confirm & Continue button if not yet completed */}
        {!isCompleted && (
          <button
            onClick={onConfirm}
            style={{
              padding: "var(--space-3) var(--space-8)",
              fontSize: "var(--text-base)",
              fontWeight: "var(--font-semibold)",
              border: "1px solid var(--cta-orange)",
              backgroundColor: "var(--cta-orange)",
              color: "white",
              cursor: "pointer",
              borderRadius: "var(--radius-md)",
              transition: "all var(--transition-fast)",
            }}
            onMouseEnter={(e) => {
              e.target.style.backgroundColor = "var(--cta-orange-hover)";
            }}
            onMouseLeave={(e) => {
              e.target.style.backgroundColor = "var(--cta-orange)";
            }}
          >
            Confirm & Continue
          </button>
        )}
      </div>
      {/* Image Preview Modal */}
      <FilePreviewModalSimple
        file={previewImage}
        isOpen={!!previewImage}
        onClose={() => setPreviewImage(null)}
      />
    </div>
  );
};

export default WallConfirmation;
